import { Schema } from "effect";
import { HttpApiEndpoint, HttpApiError, HttpApiGroup } from "effect/http-api";

const nullableString = Schema.NullOr(Schema.String);
const sniCode = Schema.String.pipe(Schema.check(Schema.isPattern(/^\d{2,5}$/u)));

export const MunicipalitySummarySchema = Schema.Struct({
  municipalityCode: Schema.String,
  municipalityName: Schema.String,
  countyCode: nullableString,
  countyName: nullableString,
  activeWorkplaceCount: Schema.Int,
  activeCompanyCount: Schema.Int,
  employeeSizeMix: Schema.Record(Schema.String, Schema.Int),
});

export const LocationCoverageSchema = Schema.Struct({
  label: Schema.Literal("Bloodline-covered SCB workplaces"),
  sourceStatus: Schema.String,
  complete: Schema.Boolean,
  asOf: nullableString,
  projectionRefreshedAt: nullableString,
  targetRefreshDays: Schema.Literal(14),
  sniClassificationVersion: Schema.Literal("SNI 2025"),
});

const SniDescriptorSchema = Schema.Struct({
  sniDivision: Schema.String,
  sniLabel: nullableString,
  sniClassificationVersion: nullableString,
});

export const MunicipalitiesResponseSchema = Schema.Struct({
  items: Schema.Array(MunicipalitySummarySchema),
  coverage: LocationCoverageSchema,
});

export const MunicipalityProfileResponseSchema = Schema.Struct({
  municipality: MunicipalitySummarySchema,
  topIndustries: Schema.Array(
    SniDescriptorSchema.pipe(
      Schema.fieldsAssign({
        activeWorkplaceCount: Schema.Int,
        activeCompanyCount: Schema.Int,
      })
    )
  ),
  coverage: LocationCoverageSchema,
});

export const IndustryProfileResponseSchema = Schema.Struct({
  items: Schema.Array(
    SniDescriptorSchema.pipe(
      Schema.fieldsAssign({
        activeWorkplaceCount: Schema.Int,
        activeCompanyCount: Schema.Int,
      })
    )
  ),
  coverage: LocationCoverageSchema,
});

export const IndustryGapsResponseSchema = Schema.Struct({
  sniDivision: Schema.String,
  sniLabel: nullableString,
  sniClassificationVersion: nullableString,
  items: Schema.Array(
    MunicipalitySummarySchema.pipe(
      Schema.fieldsAssign({
        industryWorkplaceCount: Schema.Int,
        industryCompanyCount: Schema.Int,
        localQuotient: Schema.Number,
      })
    )
  ),
  coverage: LocationCoverageSchema,
});

export const CompanyGapsResponseSchema = Schema.Struct({
  orgnr: Schema.String,
  items: Schema.Array(
    MunicipalitySummarySchema.pipe(
      Schema.fieldsAssign({
        ...SniDescriptorSchema.fields,
        industryWorkplaceCount: Schema.Int,
        industryCompanyCount: Schema.Int,
      })
    )
  ),
  coverage: LocationCoverageSchema,
});

const municipalityCode = Schema.String;
const orgnr = Schema.String;

export const LocationIntelligenceV3Group = HttpApiGroup.make("locationIntelligenceV3")
  .add(
    HttpApiEndpoint.get("listMunicipalities", "/location-intelligence/municipalities", {
      query: Schema.Struct({
        sniDivision: Schema.optional(sniCode),
        countyCode: Schema.optional(Schema.String),
      }),
      success: MunicipalitiesResponseSchema,
      error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
    })
  )
  .add(
    HttpApiEndpoint.get(
      "getMunicipalityProfile",
      "/location-intelligence/municipalities/:municipalityCode",
      {
        params: { municipalityCode },
        query: Schema.Struct({ sniDivision: Schema.optional(sniCode) }),
        success: MunicipalityProfileResponseSchema,
        error: [
          HttpApiError.BadRequestNoContent,
          HttpApiError.NotFoundNoContent,
          HttpApiError.InternalServerErrorNoContent,
        ],
      }
    )
  )
  .add(
    HttpApiEndpoint.get("getIndustryProfile", "/location-intelligence/industries", {
      query: Schema.Struct({ sniDivision: Schema.optional(sniCode) }),
      success: IndustryProfileResponseSchema,
      error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
    })
  )
  .add(
    HttpApiEndpoint.get("getIndustryGaps", "/location-intelligence/gaps/industry", {
      query: Schema.Struct({
        sniDivision: Schema.String.pipe(Schema.check(Schema.isPattern(/^\d{2}$/u))),
      }),
      success: IndustryGapsResponseSchema,
      error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
    })
  )
  .add(
    HttpApiEndpoint.get("getCompanyGaps", "/location-intelligence/gaps/company/:orgnr", {
      params: { orgnr },
      success: CompanyGapsResponseSchema,
      error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
    })
  );
